import { type CheckboxProps, styled, Checkbox as TamaguiCheckbox, YStack } from "tamagui";
import { Check as CheckIcon, Minus as MinusIcon } from "@tamagui/lucide-icons";
import { radiusTokens } from "../themes/token-radius";
import { borderSizeTokens } from "../themes/token-space";

/**
 * Checkbox component with built-in styling and a checkmark icon
 */
const borderWidth = borderSizeTokens.border2;
const borderRadius = radiusTokens.br1;
const StyledCheckbox = styled(TamaguiCheckbox, {
  name: "Checkbox",
  position: "relative",
  width: "$si3",
  height: "$si3",
  borderRadius: borderRadius,
  borderWidth: borderWidth,
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
  padding: borderWidth,
  cursor: "pointer",
  backgroundColor: "$surfaceStrong",
  unstyled: true,
  variants: {
    checked: {
      true: {
        backgroundColor: "$surfaceStrong",
        borderColor: "$surfaceStrong"
      },
      indeterminate: {
        backgroundColor: "$surfaceStrong",
        borderColor: "$surfaceStrong"
      },
      false: {
        backgroundColor: "$borderStrong",
        borderColor: "$borderStrong"
      }
    },
    disabled: {
      true: {
        backgroundColor: "$bgDisabled",
        borderColor: "$bgDisabled"
      }
    }
  } as const,
  focusStyle: {
    outlineWidth: borderSizeTokens.border3,
    outlineColor: "$borderIntense",
    outlineStyle: "solid",
    outlineOffset: 1
  },
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "bouncy",
  animateOnly: ["borderColor"]
});
const Inner = styled(YStack, {
  name: "CheckboxInner",
  position: "absolute",
  inset: 0,
  borderRadius: borderRadius - borderWidth,
  backgroundColor: "$surface2",
  variants: {
    checked: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    indeterminate: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    disabled: {
      true: {
        backgroundColor: "$surface0"
      }
    }
  } as const,
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "quick"
});
const StyledCheckIcon = styled(CheckIcon, {
  size: "$iconSmall",
  color: "$surface2",
  variants: {
    disabled: {
      true: {
        color: "$surface0"
      },
      false: {
        color: "$iconInverse"
      }
    }
  } as const,
  defaultVariants: {
    disabled: false
  }
});
const StyledMinusIcon = styled(MinusIcon, {
  size: "$iconSmall",
  color: "$surface2",
  variants: {
    disabled: {
      true: {
        color: "$surface0"
      },
      false: {
        color: "$iconInverse"
      }
    }
  } as const,
  defaultVariants: {
    disabled: false
  }
});
export const Checkbox = ({
  checked,
  ...props
}: CheckboxProps) => {
  const isIndeterminate = checked === "indeterminate";
  const isMarked = checked === true || isIndeterminate;
  return <StyledCheckbox checked={checked} transition={["bouncy", {
    delay: 500
  }]} {...props} data-sentry-element="StyledCheckbox" data-sentry-component="Checkbox" data-sentry-source-file="Checkbox.tsx">
      <Inner checked={isMarked} disabled={props.disabled} transition={[isMarked ? "quick" : "bouncy", {
      delay: !isMarked ? 0 : 120
    }]} data-sentry-element="Inner" data-sentry-source-file="Checkbox.tsx" />
      <YStack justifyContent="center" alignItems="center" scale={isMarked ? 1 : 0.7} opacity={isMarked ? 1 : 0} transition={[isMarked ? "bouncy" : "quick", {
      delay: isMarked ? 120 : 0
    }]} animateOnly={["transform", "opacity"]} pointerEvents="none" data-sentry-element="YStack" data-sentry-source-file="Checkbox.tsx">
        {isIndeterminate ? <StyledMinusIcon disabled={props.disabled} /> : <StyledCheckIcon disabled={props.disabled} />}
      </YStack>
    </StyledCheckbox>;
};